import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { api } from "../api";

interface AuthUser {
	id: string;
	username: string;
	role: string;
}

type LoginMode = "local" | "azure";

interface AuthContextType {
	gatewayMode: boolean;
	loginMode: LoginMode;
	oauthCatcherAvailable: boolean;
	user: AuthUser | null;
	isLoading: boolean;
	login: (username: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [gatewayMode, setGatewayMode] = useState(false);
	const [loginMode, setLoginMode] = useState<LoginMode>("local");
	const [oauthCatcherAvailable, setOauthCatcherAvailable] = useState(false);
	const [user, setUser] = useState<AuthUser | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// On mount: fetch gateway status, then me if in gateway mode
	useEffect(() => {
		let cancelled = false;

		async function init() {
			try {
				const status = await api.getGatewayStatus();
				if (cancelled) return;
				setGatewayMode(status.gatewayMode);
				setLoginMode(status.loginMode === "azure" ? "azure" : "local");
				setOauthCatcherAvailable(status.oauthCatcherAvailable === true);

				if (status.gatewayMode) {
					try {
						const me = await api.getMe();
						if (!cancelled) setUser(me);
					} catch {
						// not authenticated — user is null
					}
				}
			} catch {
				// network error — behave as open
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void init();
		return () => {
			cancelled = true;
		};
	}, []);

	// Register a 401 listener so any subsequent fetch failure clears the user
	useEffect(() => {
		if (!gatewayMode) return;
		const unsub = api.onUnauthorized(() => setUser(null));
		return unsub;
	}, [gatewayMode]);

	const login = useCallback(async (username: string, password: string) => {
		const result = await api.login({ username, password });
		setUser({ id: "", username: result.username, role: result.role });
	}, []);

	const logout = useCallback(async () => {
		await api.logout();
		setUser(null);
	}, []);

	const value = useMemo(
		() => ({
			gatewayMode,
			loginMode,
			oauthCatcherAvailable,
			user,
			isLoading,
			login,
			logout,
		}),
		[
			gatewayMode,
			loginMode,
			oauthCatcherAvailable,
			user,
			isLoading,
			login,
			logout,
		],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within AuthProvider");
	return ctx;
}
